import { useEffect, useState, useMemo } from 'react';
import { mapValues } from 'lodash-es';
import jobListing from '../../config/FormConfigs/jobListing';
import { initIndex } from '../../Callbacks/algoliaHelper/searchClient';
import ListingTemplateJobs from '../../Components/Templates/ListingTemplateJobs';
import JobsCollection from '../../Components/Organisms/Lists/JobsCollection';
import { collectFacetQuery } from '../../utils/algoliaFiltrationHelper';
import ListingMeta from '../../Components/Organisms/ListingMeta/ListingMeta';
import { useAnalyticsPushEvent } from '../../analytic/analytic.context';
import { getNumberOfResultsString } from '../../utils/analitic.utils';
import useQueryParam from '../../hooks/useQueryParam';
import getPagedResults from '../../utils/getPagedResults';
import replaceSearchQuerySeparators from '../../utils/replaceSearchQuerySeparators';
import { filFacetsWithCount, fillFacets } from '../../utils/fillJobWithOptions';

let isInitialized = false;

const paramNamesMap = {
  hitsPerPage: 'hitsPerPage',
  query: 'query',
  field_country: 'fieldCountry',
  field_state: 'fieldState',
  field_city: 'fieldCity',
  field_job_function: 'fieldJobFunction',
  field_target_profile: 'fieldProfile',
};

function updateUrlWithFilters(filters, sortByState) {
  if (!window) {
    return;
  }
  const queryParams = new URLSearchParams(window.location.search);
  const { protocol, pathname, host } = window.location;

  // Add filters to queryParams
  Object.keys(filters).forEach((key) => {
    const val =
      typeof filters[key] === 'object' ? filters[key].join(',') : filters[key];
    if (val.trim() !== '') {
      queryParams.set(paramNamesMap[key], val);
    } else {
      queryParams.delete(paramNamesMap[key]);
    }
  });

  // Only add sort parameter if sortByState has changed from default
  if (sortByState !== 'default') {
    let simplifiedSort;
    if (sortByState.includes('recent')) {
      simplifiedSort = 'replica_recent';
    } else if (sortByState.includes('A-Z')) {
      simplifiedSort = 'replica_A-Z';
    } else {
      simplifiedSort = 'replica_Z-A';
    }
    queryParams.set('sort', simplifiedSort);
  }

  let newUrl;
  if (queryParams.toString()) {
    newUrl = `${protocol}//${host}${pathname}?${queryParams.toString()}`;
    window.history.pushState({}, '', newUrl);
  } else {
    newUrl = `${protocol}//${host}${pathname}`;
    window.history.pushState({}, '', newUrl);
  }
}

const JobsListing = ({ props }) => {
  const { indexName, filters, showSelectedSort } = props;
  const algoliaSettings = window.drupalSettings.careersSearch;
  const { replicas } = algoliaSettings;
  const [hitsPerPage] = useQueryParam('hitsPerPage');
  const [fieldQuery] = useQueryParam('query');
  const [fieldCountry] = useQueryParam('fieldCountry');
  const [fieldState] = useQueryParam('fieldState');
  const [fieldCity] = useQueryParam('fieldCity');
  const [fieldJobFunction] = useQueryParam('fieldJobFunction');
  const [fieldProfile] = useQueryParam('fieldProfile');
  const initialFiltersState = {
    hitsPerPage: hitsPerPage || '',
    query: fieldQuery || '',
    field_country: fieldCountry || '',
    field_state: fieldState || '',
    field_city: fieldCity || '',
    field_job_function: fieldJobFunction || '',
    field_target_profile: fieldProfile || '',
  };

  const [index, setIndex] = useState(initIndex(indexName));
  const [sortByState, setSortByState] = useState('default'); // Initialize to 'default'
  const [filtersState, setFiltersState] = useState(initialFiltersState);
  const [queryState, setQueryState] = useState({ query: '' });
  const [queryPlaceHolder, setQueryPlaceHolder] = useState('');
  const [page, setPage] = useState(0);
  const [result, setResult] = useState(null);
  const emptyFilters = {
    hitsPerPage: '',
    query: '',
    field_country: '',
    field_state: '',
    field_city: '',
    field_job_function: '',
    field_target_profile: '',
  };

  const changePage = (newPage) => {
    setPage(newPage);
  };

  const clearCallback = () => {
    setFiltersState(emptyFilters);
    setQueryState('');
  };

  const buildFiltersList = (filterList, filterModel) => {
    const newFilters = { ...filterModel };

    function createOptions(filter) {
      return filter.terms.map((term) => ({
        label: term.label,
        value: term.value,
      }));
    }

    Object.keys(newFilters).forEach((key) => {
      const filter = filterList?.find(
        (term) => term.name.toLowerCase() === key.toLowerCase(),
      );

      if (filter) {
        newFilters[key].options = createOptions(filter);
      }
    });

    return newFilters;
  };

  const filtersData = useMemo(
    () => buildFiltersList(filters, jobListing),
    [filters],
  );

  const pushAnalyticEvent = useAnalyticsPushEvent();
  const [viewSearchResultsAnalyticsData, setViewSearchResultsAnalyticsData] =
    useState(undefined);

  const search = (updatePager = false, viewSearchResultsAnalytics) => {
    if (updatePager && page !== 0) {
      if (viewSearchResultsAnalytics)
        setViewSearchResultsAnalyticsData(viewSearchResultsAnalytics);
      setPage(0);
    } else {
      const query = replaceSearchQuerySeparators(filtersState.query);
      const facetFiltersString = collectFacetQuery({ ...filtersState });
      index
        .search(query, {
          filters,
          facets: ['*'],
          facetFilters: facetFiltersString,
          page,
          hitsPerPage: filtersState?.hitsPerPage || 12,
          attributesToRetrieve: [
            'nid',
            'title',
            'page',
            'hitsPerPage',
            'fieldProfile',
            'sort',
            'fieldJobFunction',
            'fieldCountry',
            'fieldState',
            'fieldCity',
          ],
        })
        .then((res) => {
          setResult(res);
          const { facets: responseFacets } = res;
          // Fill facet filters with options.
          if (responseFacets && Object.keys(responseFacets).length) {
            fillFacets(jobListing.topFilters, responseFacets);
          }
          // If we don't have any results we should show facets, filled with zeros.
          if (!res?.hits.length) {
            const resetedFacetsDefault = mapValues(responseFacets, (o) =>
              Object.keys(o).reduce((acc, curr) => ({ ...acc, [curr]: 0 }), {}),
            );
            const filledFacets = fillFacets(
              jobListing.topFilters,
              resetedFacetsDefault,
            );
            filFacetsWithCount(jobListing.topFilters, filledFacets);
          }

          if (!isInitialized) {
            setQueryPlaceHolder();
          }
          isInitialized = true;

          viewSearchResultsAnalytics =
            viewSearchResultsAnalytics || viewSearchResultsAnalyticsData;
          if (viewSearchResultsAnalytics) {
            pushAnalyticEvent({
              ...viewSearchResultsAnalytics,
              search_term: queryState.query,
              number_of_results: getNumberOfResultsString(res?.hits?.length),
              content_type: 'job_posting',
              element_location:
                viewSearchResultsAnalytics.element_location || 'listing',
            });
            setViewSearchResultsAnalyticsData(undefined);
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Search error:', error);
          isInitialized = true;
        });
    }
  };

  useEffect(() => {
    search();
  }, [filtersState, queryState.query]);

  useEffect(() => {
    if (page !== 0) {
      setPage(0);
    } else if (isInitialized) {
      search();
    }
    // Only update the URL if sortByState has changed from default
    if (sortByState !== 'recent') {
      updateUrlWithFilters(filtersState, sortByState); // Pass the sortByState here
    }
  }, [filtersState, sortByState]);

  // Call search callback on page change.
  useEffect(search, [page]);

  const pagerData = useMemo(() => {
    if (!result) {
      return {};
    }

    return {
      analyticData: {
        listingType: 'job_listing',
        searchTerm: queryState.query,
      },
      meta: {
        totalRows: result.nbHits,
        pageSize: result.hitsPerPage,
        totalPages: result.nbPages,
        pageNumber: result.page,
      },
      changePage,
      itemsPerPage: {
        settings: {
          name: 'itemsPerPage',
          label: Drupal.t('Items per page'),
          hideLabel: true,
          options: [
            {
              value: 12,
              label: `12 ${Drupal.t('results per page')}`,
            },
            {
              value: 24,
              label: `24 ${Drupal.t('results per page')}`,
            },
            {
              value: 48,
              label: `48 ${Drupal.t('results per page')}`,
            },
          ],
          state: { itemsPerPage: filtersState?.hitsPerPage },
        },
        onChange: (event) => {
          setFiltersState((prev) => ({
            ...prev,
            hitsPerPage: event.target.value,
          }));
        },
      },
    };
  }, [result, queryState]);

  const sortByOptions = useMemo(() => {
    return [
      {
        label: Drupal.t('Recent'),
        value: replicas.find((e) => e.includes('replica_recent')),
      },
      {
        label: Drupal.t('A-Z'),
        value: replicas.find((e) => e.includes('replica_A-Z')),
      },
      {
        label: Drupal.t('Z-A'),
        value: replicas.find((e) => e.includes('replica_Z-A')),
      },
    ];
  }, []);

  if (!result) {
    return '';
  }

  let resultArray;
  if (queryState.query) {
    resultArray = result.hits.filter(
      (queryFilter) => queryFilter.title === queryState.query,
    );
  }

  if (!result) return <div>Loading...</div>;
  const pagedItems = getPagedResults(
    result.page,
    result.hitsPerPage,
    result.nbHits,
  );

  const metaSettings = {
    itemsPerPage: result.hitsPerPage,
    totalResults: result.nbHits,
    sortByState,
    sortByOptions,
    setSortByState: (v) => {
      setIndex(initIndex(v));
      setSortByState(v); // This will update the sortByState and trigger URL update
    },
    bottomSort: true,
  };
  const baseUrl = window.location.origin;
  const resultMap = queryState.query ? resultArray : result.hits;
  const mappedResults = resultMap.map((hit) => ({
    nid: hit.nid,
    title:
      // eslint-disable-next-line no-underscore-dangle
      hit.title || hit._highlightResult.field_job_title?.value || 'No Title',
    // eslint-disable-next-line no-underscore-dangle
    country: hit.field_country || hit._highlightResult.field_country?.value,
    // eslint-disable-next-line no-underscore-dangle
    city: hit.field_city || hit._highlightResult.field_city?.value,
    states:
      // eslint-disable-next-line no-underscore-dangle
      hit.field_state || hit._highlightResult.field_state?.value,
    job_level:
      hit.field_target_profile ||
      // eslint-disable-next-line no-underscore-dangle
      hit._highlightResult.field_target_profile?.value,
    brand_title:
      hit.field_logo_title ||
      // eslint-disable-next-line no-underscore-dangle
      hit._highlightResult.field_logo_title?.value ||
      'No Brand Title',
    brand:
      hit.field_logo_url || // eslint-disable-next-line no-underscore-dangle
      hit._highlightResult.field_logo_url?.value ||
      '/themes/custom/sgna/images/logo.svg',
    post_date: hit.field_posting_start_date
      ? new Date(
          Number(hit.field_posting_start_date) * 1000,
        ).toLocaleDateString()
      : new Date(
          // eslint-disable-next-line no-underscore-dangle
          Number(hit._highlightResult.field_posting_start_date?.value) * 1000,
        ).toLocaleDateString(),

    cta_link: `${baseUrl}${
      // eslint-disable-next-line no-underscore-dangle
      hit.job_posting_url || hit._highlightResult.job_posting_url?.value
    }`,
  }));

  return (
    <div className="container">
      <ListingTemplateJobs
        pagerData={pagerData}
        queryState={queryState}
        setQueryState={setQueryState}
        filtersConfig={filtersData.topFilters}
        filtersState={filtersState}
        setFiltersState={setFiltersState}
        onSearch={search}
        showSelectedSort
        queryPlaceHolder={queryPlaceHolder}
        clearAllCallback={clearCallback}
      >
        <div className="jobs-collection-item__header">
          {metaSettings && (
            <ListingMeta
              {...metaSettings}
              pagedItems={pagedItems}
              showSelectedSort={showSelectedSort}
            />
          )}
        </div>
        {mappedResults && mappedResults.length > 0 ? (
          <JobsCollection products={mappedResults} />
        ) : (
          <div className="search-no-results">
            <h4>
              {Drupal.t(
                'There are no results for this query. Please refine your search.',
              )}
            </h4>
          </div>
        )}
      </ListingTemplateJobs>
    </div>
  );
};

export default JobsListing;
